import _ from "lodash";
import React, { useContext, useState } from "react";
import { NextSeo } from "next-seo";
import { Collapse, ButtonGroup } from "reactstrap";
import Icon from "@mdi/react";
import { mdiCheck, mdiClose, mdiHelpCircle } from "@mdi/js";

import GAService from "../services/GAService";
import {
  FEATURE_LIST,
  ACTIVE_PRICING_DURATIONS,
  mobileSubscriptionLookup,
  ProductDuration,
  useUserSubscriptions,
  productDurations,
  ALL_TIERS,
  getProductPurchaseModel,
  SubscriptionContext,
  ALT_TIERS,
} from "../services/SubscriptionService";
import AnalyticsService from "../services/AnalyticsService";
import { FeatureItem, TierItem } from "../typings/pricing";
import Accordion from "../components/Accordion";
import { Button } from "../components/Button";
import Popover from "../components/Popover";
import styles from "../styles/pricing.scss";
import CheckoutModal from "src/components/Pricing/CheckoutModal";
import MobileSubModal from "src/components/Pricing/MobileSubModal";
import CancelModal from "src/components/Pricing/CancelModal";
import UpgradeModal from "src/components/Pricing/UpgradeModal";
import DowngradeModal from "src/components/Pricing/DowngradeModal";
import ReactivateModal from "src/components/Pricing/ReactivateModal";
import Link from "next/link";
import MarketingVideo from "src/components/MarketingVideo";
import { useRouter } from "next/router";
import { usePortal } from "src/components/Portal";
import { useFreeTrialClaimed } from "src/services/UserService";
import { MgmBanner } from "src/components/OfferBanner";
import { DisplayIfLegalState } from "src/components/DisplayIfLegalState";

const BloombergLogo = require("public/logos/bloomberg@3x.png?sizes[]=161,sizes[]=322,sizes[]=483");
const BusinessInsiderLogo = require("public/logos/business-insider@3x.png?sizes[]=93,sizes[]=186,sizes[]=279");
const ForbesLogo = require("public/logos/forbes@3x.png?sizes[]=87,sizes[]=174,sizes[]=261");
const BostonGlobeLogo = require("public/logos/the-boston-globe@3x.png?sizes[]=186,sizes[]=372,sizes[]=558");
const NewYorkTimesLogo = require("public/logos/the-new-york-times@3x.png?sizes[]=211,sizes[]=422,sizes[]=633");

const FAQ_LIST: { title: string; body: string }[] = [
  {
    title: "What payment methods are accepted?",
    body: "We accept all major credit cards.",
  },
  {
    title: "Can I cancel at any time?",
    body: 'Yes, you may cancel your membership at any time.  There is no long-term commitment.  To avoid future charges, simply cancel your membership before your next renewal date. For more information, please refer to <a href="https://support.betql.co/hc/en-us/categories/360003861073-FAQs" target="_blank" rel="noopener">this article</a>.',
  },
  {
    title: "Can I pause my membership when my primary sport enters the off-season?",
    body: "Unfortunately, it is not possible to pause and resume a BetQL membership at this time.",
  },
  {
    title: "What is your refund policy?",
    body: 'Refund requests are honored when they are received within 24 hours of payment.  Refund requests should be sent as a <a href="https://support.betql.co/hc/en-us/requests/new" target="_blank" rel="noopener">support ticket</a>.',
  },
  {
    title: "Where can I send any additional questions?",
    body: 'Contact our support team by submitting a support ticket here: <a href="https://support.betql.co/hc/en-us/requests/new" target="_blank" rel="noopener">support.betql.co</a> ',
  },
];

const urlToDuration: any = {
  monthly: "month",
  annual: "year",
};

const durationToUrl = _.invert(urlToDuration);

const tabTitle = {
  month: "Monthly",
  year: "Annual",
};

const tabDiscount = {
  month: "",
  year: "50% off Monthly",
};

type CheckoutAction =
  | "purchase"
  | "subscribe"
  | "upgrade"
  | "downgrade"
  | "cancel"
  | "reactivate"
  | "mobile"
  | "nothing";

/**
 * What should the buy button do for the given subscription/consumable?
 * @param userSubs Passed in from useUserSubscriptions
 * @param subKey The key of the subscription in question
 */
const getCheckoutAction = (userSubs: any[], subKey: string): CheckoutAction => {
  const tierInCart = ALL_TIERS[subKey];
  const purchaseModelInCart = getProductPurchaseModel(tierInCart.key);

  const userSubKeys = userSubs.map((x: any) => x.subscriptionLevel.name);
  const userRecurringSub = userSubs[userSubKeys.map(getProductPurchaseModel).indexOf("subscription")]; // prettier-ignore
  const userRecurringSubKey = userRecurringSub?.subscriptionLevel.name;
  const userRecurringTier: any = ALL_TIERS[userRecurringSubKey] ?? ALT_TIERS[userRecurringSubKey]; // prettier-ignore

  const hasMobileSub = userSubKeys.some((x) => mobileSubscriptionLookup[x]);
  const hasConsumable = userSubKeys.map(getProductPurchaseModel).includes("consumable"); // prettier-ignore

  if (hasMobileSub) {
    return "mobile";
  }

  if (purchaseModelInCart === "consumable") {
    return hasConsumable ? "nothing" : "purchase";
  }

  if (!userRecurringSub) {
    return "subscribe";
  }

  if (userRecurringTier) {
    if (tierInCart.key === userRecurringTier.key) {
      if (userRecurringSub.cancelAt) {
        return "reactivate";
      }
      return "cancel";
    }

    if (tierInCart.totalPrice < userRecurringTier.totalPrice) {
      return "downgrade";
    }
    return "upgrade";
  }

  // Make TypeScript happy
  return "nothing";
};

/**
 * Start checkout flow for sub key
 */
const useCheckoutFlow = ({ subKey }: { subKey: string }) => {
  const ctx = useContext(SubscriptionContext);

  const userSubs = useUserSubscriptions();
  const userSubKeys = userSubs.map((x: any) => x.subscriptionLevel.name);
  const userRecurringSub = userSubs[userSubKeys.map(getProductPurchaseModel).indexOf("subscription")]; // prettier-ignore
  const userRecurringSubKey = userRecurringSub?.subscriptionLevel.name;
  const userRecurringTier: any = ALL_TIERS[userRecurringSubKey] ?? ALT_TIERS[userRecurringSubKey]; // prettier-ignore

  const tierInCart = ALL_TIERS[subKey];
  const upsellTier: any = tierInCart.key.includes("month") && ALL_TIERS[tierInCart.key.replace("month", "year")];

  const analyticsProps = {
    screen: "PricingView",
    page: "Pricing",
    tab: tierInCart.durationTitle,
    subscriptionLevel: `${tierInCart.title} ${tierInCart.durationTitle}`,
    upgradeSource: ctx.subscriptionUpgradeSource,
  };

  const [, setCheckoutVisible] = usePortal(
    <CheckoutModal
      visible={true}
      selectedTier={tierInCart}
      toggleModal={() => setCheckoutVisible(false)}
      upgradeSource={ctx.subscriptionUpgradeSource}
      upsellTier={upsellTier}
    />
  );

  const subscribeOrPurchase = () => {
    setCheckoutVisible(true);
    AnalyticsService.track("Checkout Button Click", {
      ...analyticsProps,
      source: "Checkout",
    });
    GAService.sendEvent({
      hitType: "event",
      eventCategory: "Begin Checkout",
      eventAction: window.location.href,
      eventLabel: tierInCart.key,
    });
  };

  const [, setUpgradeVisible] = usePortal(
    <UpgradeModal
      visible={true}
      currentTier={userRecurringTier}
      selectedTier={tierInCart}
      toggleModal={() => setUpgradeVisible(false)}
      signedIn={ctx.signedIn}
      userSubscription={userRecurringSub}
    />
  );

  const upgrade = () => {
    setUpgradeVisible(true);
    AnalyticsService.track("Checkout Button Click", {
      ...analyticsProps,
      source: "Upgrade",
    });
  };

  const [, setDowngradeVisible] = usePortal(
    <DowngradeModal
      visible={true}
      currentTier={userRecurringTier}
      selectedTier={tierInCart}
      toggleModal={() => setDowngradeVisible(false)}
      signedIn={ctx.signedIn}
      userSubscription={userRecurringSub}
    />
  );

  const downgrade = () => {
    setDowngradeVisible(true);
    AnalyticsService.track("Checkout Button Click", {
      ...analyticsProps,
      source: "Downgrade",
    });
  };

  const [, setCancelVisible] = usePortal(
    <CancelModal
      visible={true}
      currentTier={userRecurringTier ?? undefined}
      toggleModal={() => setCancelVisible(false)}
      userSubscription={userRecurringSub}
    />
  );

  const cancel = () => {
    setCancelVisible(true);
    AnalyticsService.track("Checkout Button Click", {
      ...analyticsProps,
      source: "Cancel",
    });
  };

  const [, setReactivateVisible] = usePortal(
    <ReactivateModal
      visible={true}
      currentTier={userRecurringTier ?? undefined}
      toggleModal={() => setReactivateVisible(false)}
      userSubscription={userRecurringSub}
    />
  );

  const reactivate = () => {
    setReactivateVisible(true);
    AnalyticsService.track("Checkout Button Click", {
      ...analyticsProps,
      source: "Reactivate",
    });
  };

  const [, setMobileVisible] = usePortal(<MobileSubModal visible={true} toggleModal={() => setMobileVisible(false)} />);

  const mobile = () => {
    setMobileVisible(true);
  };

  const actions = {
    purchase: subscribeOrPurchase,
    subscribe: subscribeOrPurchase,
    upgrade,
    downgrade,
    cancel,
    reactivate,
    mobile,
    nothing: () => {},
  };

  const checkoutAction = getCheckoutAction(userSubs, tierInCart.key);
  const checkout = actions[checkoutAction];

  return { checkoutAction, checkout };
};

const CheckoutButton = ({
  subKey,
  className,
  ...other
}: {
  subKey: string;
  className?: string;
  [key: string]: any;
}) => {
  const { checkoutAction, checkout } = useCheckoutFlow({ subKey });

  const isOwned = ["cancel", "reactivate", "nothing"].includes(checkoutAction); // prettier-ignore

  return (
    <Button
      className={`checkout-button ${isOwned ? "current" : ""} ${className || ""}`} // prettier-ignore
      onClick={checkout}
      disabled={checkoutAction === "nothing"}
      {...other}
    >
      {
        {
          subscribe: "START TRIAL",
          purchase: "START TRIAL",
          upgrade: "UPGRADE",
          downgrade: "DOWNGRADE",
          cancel: "Current Plan",
          reactivate: "Current Plan",
          mobile: "START TRIAL",
          nothing: "Current Plan",
        }[checkoutAction]
      }
    </Button>
  );
};

const CancelReactivateButton = ({ subKey }: { subKey: string }) => {
  const { checkoutAction, checkout } = useCheckoutFlow({ subKey });

  if (checkoutAction === "cancel") {
    return (
      <div className="cancel-btn-bottom" onClick={checkout}>
        Cancel
      </div>
    );
  }

  if (checkoutAction === "reactivate") {
    return (
      <div className="cancel-btn-bottom" onClick={checkout}>
        Reactivate
      </div>
    );
  }

  return null;
};

const MobileFeatureList = ({ tier }: { tier: TierItem }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Collapse isOpen={open}>
        {FEATURE_LIST.map((feature: FeatureItem, featureIndex: number) => {
          return tier.features.includes(feature.key) ? (
            <div key={featureIndex} className="rotoql-content-pricing__feature-container">
              <div className="rotoql-content-pricing__feature-title">{feature.title}</div>
              <div className="rotoql-content-pricing__feature-body">{feature.body}</div>
            </div>
          ) : null;
        })}
      </Collapse>
      <button onClick={() => setOpen(!open)} className="rotoql-content-pricing__features-btn">
        {open ? "Hide" : "See"} Features
      </button>
    </>
  );
};

const Pricing = () => {
  const ctx = useContext(SubscriptionContext);
  const router = useRouter();
  const trialClaimed = useFreeTrialClaimed();

  const duration: ProductDuration | undefined = urlToDuration[router?.query?.duration as any];

  const setDuration = (_duration: ProductDuration) => {
    router.push(`/pricing/${durationToUrl[_duration]}`);
  };

  const onStartNow = () => {
    ctx.togglePricingModal({
      deepLink: "auth",
    });
  };

  if (!duration) {
    return null;
  }

  return (
    // HEADER & NAV
    <div className="rotoql-content-pricing">
      <div className="rotoql-content-pricing__main-container">
        <NextSeo
          title="Pricing | BetQL"
          openGraph={{
            url: "https://www.betql.co/pricing",
            title: "Pricing | BetQL",
          }}
        />
        <DisplayIfLegalState>
          <div className="free-mgm">
            <MgmBanner onCtaClick={onStartNow} />
          </div>
        </DisplayIfLegalState>
        <div className="rotoql-content-pricing__header-container">
          <div className="rotoql-content-pricing__title-container">
            <div className="rotoql-content-pricing__subtitle-text">Access to Both BetQL Web and Mobile App</div>
            <div className="rotoql-content-pricing__title-text">
              {!trialClaimed && <div className="rotoql-content-pricing__free-trial-text">Free 3 Day Trial with</div>}
              {tabTitle[duration]} Pass
            </div>
            <Link href="/how-it-works" legacyBehavior>
              <a className="rotoql-content-pricing__title-link">Learn More About the Product</a>
            </Link>
            <MarketingVideo className="rotoql-content-pricing__marketing-video" />
          </div>
          <div className="rotoql-content-pricing__duration-container">
            <ButtonGroup className="rotoql-content-pricing__duration-btn-group">
              {productDurations.map((_duration, index: number) => {
                return (
                  <button
                    key={index}
                    className={`rotoql-content-pricing__duration-button ${
                      _duration === duration ? "rotoql-content-pricing__duration-button--active" : ""
                    }`}
                    onClick={() => setDuration(_duration)}
                  >
                    <div className="rotoql-content-pricing__duration-text">{tabTitle[_duration]}</div>
                    {tabDiscount[_duration] ? (
                      <div className="rotoql-content-pricing__duration-subtext">{tabDiscount[_duration]}</div>
                    ) : null}
                  </button>
                );
              })}
            </ButtonGroup>
          </div>
        </div>

        {/* MONTHLY & ANNUAL VIEW */}
        {duration && (
          <div className="rotoql-content-pricing__table-container">
            <div className="rotoql-content-pricing__feature-column">
              <div className="rotoql-content-pricing__feature-column-title">
                <div>
                  Number of Sports,
                  <br />
                  Pricing &amp; Features
                </div>

                <div className="rotoql-content-pricing__change-sport-reminder">
                  *You can change your sport 1x per week
                </div>
              </div>
              {/* re-align grid when "3 DAYS FREE" is shown */}
              {!trialClaimed && <div style={{ height: 52 }} />}
              {FEATURE_LIST.map((feature: FeatureItem, featureIndex: number) => {
                return (
                  <div key={featureIndex} className="rotoql-content-pricing__feature-column-row">
                    {feature.title}
                    <div
                      onClick={() =>
                        AnalyticsService.track("See Plans", {
                          screen: "PricingView",
                          page: "Pricing",
                          tab: tabTitle[duration],
                          feature: feature.key,
                          upgradeSource: ctx.subscriptionUpgradeSource,
                        })
                      }
                      id={`feature-column-row-icon-${featureIndex}`}
                      className="rotoql-content-pricing__feature-column-row-icon"
                    >
                      <Icon path={mdiHelpCircle} size="20px" color="#000000" />
                    </div>
                    <Popover
                      className="rotoql-content-pricing__feature-popover"
                      target={`feature-column-row-icon-${featureIndex}`}
                    >
                      <div className="rotoql-content-pricing__feature-title">{feature.title}</div>
                      <div className="rotoql-content-pricing__feature-body">{feature.body}</div>
                    </Popover>
                  </div>
                );
              })}
            </div>
            {ACTIVE_PRICING_DURATIONS.find((x) => x.key === duration)?.tiers.map(
              (tier: TierItem, tierIndex: number) => {
                return (
                  <div key={tierIndex} className={"rotoql-content-pricing__tier-container"}>
                    {tier.badge ? (
                      <div
                        className="rotoql-content-pricing__tier-corner-badge"
                        style={{
                          borderBottomColor: tier.badge.backgroundColor,
                        }}
                      >
                        <div className="rotoql-content-pricing__tier-corner-badge-text">{tier.badge.text}</div>
                      </div>
                    ) : null}
                    <div style={{ height: 15 }} />
                    {!trialClaimed && <div className="rotoql-content-pricing__tier-trial-text">3 DAYS FREE</div>}
                    <div className="rotoql-content-pricing__tier-title">{tier.title}</div>

                    <div className="rotoql-content-pricing__tier-price-duration">
                      <span className="rotoql-content-pricing__price-text">${tier.price.toFixed(2)}</span>/
                      {tier.durationDisplay}
                    </div>

                    <div className="rotoql-content-pricing__tier-description">{tier.description}</div>
                    <CheckoutButton subKey={tier.key} className="tier-buy-btn" />
                    <MobileFeatureList tier={tier} />
                    {FEATURE_LIST.map((feature: FeatureItem, featureIndex: number) => {
                      return (
                        <div
                          key={featureIndex}
                          className={"rotoql-content-pricing__feature-row"}
                          style={
                            featureIndex === FEATURE_LIST.length - 1
                              ? {
                                  borderBottom: 0,
                                }
                              : {}
                          }
                        >
                          {tier.features.includes(feature.key) ? (
                            <Icon path={mdiCheck} size="30px" color="#262626" />
                          ) : (
                            <Icon path={mdiClose} size="30px" color="#afafaf" />
                          )}
                        </div>
                      );
                    })}
                    <CancelReactivateButton subKey={tier.key} />
                  </div>
                );
              }
            )}
          </div>
        )}
        {/* PUBLICATIONS SECTION */}
        <div className="rotoql-content-pricing__publications-container">
          <img
            data-src={NewYorkTimesLogo.src}
            data-srcset={NewYorkTimesLogo.srcSet}
            style={{ width: "211px" }}
            alt="The New York Times Logo"
            className="rotoql-content-pricing__publication-logo lazyload"
          />
          <img
            data-src={ForbesLogo.src}
            data-srcset={ForbesLogo.srcSet}
            style={{ width: "87px" }}
            alt="Forbes Logo"
            className="rotoql-content-pricing__publication-logo lazyload"
          />
          <img
            data-src={BloombergLogo.src}
            data-srcset={BloombergLogo.srcSet}
            style={{ width: "161px" }}
            alt="Bloomberg Logo"
            className="rotoql-content-pricing__publication-logo lazyload"
          />
          <img
            data-src={BusinessInsiderLogo.src}
            data-srcset={BusinessInsiderLogo.srcSet}
            style={{ width: "93px" }}
            alt="Business Insider Logo"
            className="rotoql-content-pricing__publication-logo lazyload"
          />
          <img
            data-src={BostonGlobeLogo.src}
            data-srcset={BostonGlobeLogo.srcSet}
            style={{ width: "186px" }}
            alt="The Boston Globe Logo"
            className="rotoql-content-pricing__publication-logo lazyload"
          />
        </div>
      </div>
      <div className="rotoql-content-pricing__full-divider" />
      {/* FAQ SECTION */}
      <div className="rotoql-content-pricing__main-container">
        <Accordion title="Frequent Questions" items={FAQ_LIST} />

        <a className="faq-review" href="https://support.betql.co/hc/en-us/categories/360003861073-FAQs" target="_blank">
          You still have questions? Visit the full FAQ page
        </a>
      </div>
      <div style={{ height: "60px" }} />
      <style jsx>{styles}</style>
    </div>
  );
};

export default Pricing;
